import { useState, useEffect, useCallback, useRef } from "react";
import { get as idbGet, set as idbSet } from "idb-keyval";

/**
 * useOfflineQueue — Ship 3 · Field Offline Safe Mode (PWA · IndexedDB).
 *
 * When the browser is offline OR a fetch fails at the network layer, POST
 * requests fired through queuedFetch() are appended to a persistent queue
 * in IndexedDB (via idb-keyval). When the browser returns online, the queue
 * drains — either in-tab (immediate) or by the Service Worker via the
 * Background Sync API when the tab is closed / screen locked.
 *
 * Why IndexedDB (vs. the old localStorage impl)?
 *   Field Mode used to be capped at 12 queued items (5MB localStorage limit
 *   with base64 photos). IndexedDB gives us hundreds of MB — a full day of
 *   Hurst Conf Center photo capture survives with room to spare.
 *
 * Service Worker handshake:
 *   • On enqueue offline, we register a `drain-field-queue` sync tag.
 *   • The SW (see /public/service-worker.js) drains from the same IDB store.
 *   • When SW finishes, it postMessages `field-queue-drained` — we refresh
 *     queueSize so the badge in the UI clears without a page reload.
 *
 * Public API is unchanged from the localStorage version so no consumer
 * code needs to change.
 *
 * @param {string} queueKey — namespace, e.g. `field:queue:${sessionId}`.
 *   MUST start with `field:queue:` so the SW's regex picks it up.
 * @returns { queuedFetch, cacheGet, cacheSet, queueSize, isOnline,
 *            draining, drain, lastDrainResult }
 */
export default function useOfflineQueue(queueKey = "field:queue:global") {
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== "undefined" ? navigator.onLine : true
  );
  const [queueSize, setQueueSize] = useState(0);
  const [draining, setDraining] = useState(false);
  const [lastDrainResult, setLastDrainResult] = useState(null);
  const drainingRef = useRef(false);

  const readQueue = useCallback(async () => {
    try {
      const v = await idbGet(queueKey);
      return Array.isArray(v) ? v : [];
    } catch { return []; }
  }, [queueKey]);

  const writeQueue = useCallback(async (q) => {
    try {
      await idbSet(queueKey, q);
      setQueueSize(q.length);
    } catch (e) {
      console.warn("[offline] IDB write failed:", e);
    }
  }, [queueKey]);

  // Initial size read
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const q = await readQueue();
      if (!cancelled) setQueueSize(q.length);
    })();
    return () => { cancelled = true; };
  }, [readQueue]);

  // Online/offline listeners
  useEffect(() => {
    const on = () => setIsOnline(true);
    const off = () => setIsOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);

  // Listen for SW-driven drains so the badge clears automatically
  useEffect(() => {
    if (typeof navigator === "undefined" || !navigator.serviceWorker) return;
    const onMsg = async (event) => {
      if (event.data && event.data.type === "field-queue-drained") {
        const q = await readQueue();
        setQueueSize(q.length);
        setLastDrainResult({
          drained: event.data.results?.[queueKey]?.drained || 0,
          failed: event.data.results?.[queueKey]?.remaining || 0,
          at: new Date().toISOString(),
          via: "service-worker",
        });
      }
    };
    navigator.serviceWorker.addEventListener("message", onMsg);
    return () => navigator.serviceWorker.removeEventListener("message", onMsg);
  }, [queueKey, readQueue]);

  const cacheGet = useCallback(async (key) => {
    try { return (await idbGet(`${queueKey}:cache:${key}`)) ?? null; }
    catch { return null; }
  }, [queueKey]);

  const cacheSet = useCallback(async (key, value) => {
    try { await idbSet(`${queueKey}:cache:${key}`, value); }
    catch (e) { console.warn("[offline] cache write failed:", e); }
  }, [queueKey]);

  // Ask the SW to schedule a background drain (best effort)
  const requestBackgroundSync = useCallback(async () => {
    if (typeof navigator === "undefined" || !navigator.serviceWorker) return;
    try {
      const reg = await navigator.serviceWorker.ready;
      if (reg.sync && typeof reg.sync.register === "function") {
        await reg.sync.register("drain-field-queue");
      }
    } catch { /* not supported — that's fine, in-tab drain covers it */ }
  }, []);

  const enqueue = useCallback(async (item) => {
    const q = await readQueue();
    q.push(item);
    await writeQueue(q);
    await requestBackgroundSync();
  }, [readQueue, writeQueue, requestBackgroundSync]);

  /**
   * queuedFetch — same signature as fetch(), but transparently queues on
   * network failure (offline or fetch reject). Returns the live Response
   * on success; returns a synthesized 202-ish stub when queued so caller
   * code doesn't crash.
   */
  const queuedFetch = useCallback(async (url, opts = {}) => {
    if (typeof navigator !== "undefined" && !navigator.onLine) {
      await enqueue({
        url,
        method: opts.method || "POST",
        headers: opts.headers || {},
        body: typeof opts.body === "string" ? opts.body : safeStringify(opts.body),
        credentials: opts.credentials || "include",
        enqueued_at: new Date().toISOString(),
      });
      return synthQueuedResponse();
    }
    try {
      return await fetch(url, opts);
    } catch (e) {
      await enqueue({
        url,
        method: opts.method || "POST",
        headers: opts.headers || {},
        body: typeof opts.body === "string" ? opts.body : safeStringify(opts.body),
        credentials: opts.credentials || "include",
        enqueued_at: new Date().toISOString(),
        error: String(e).slice(0, 140),
      });
      return synthQueuedResponse();
    }
  }, [enqueue]);

  const drain = useCallback(async () => {
    if (drainingRef.current) return { drained: 0, failed: 0 };
    drainingRef.current = true;
    setDraining(true);
    const q = await readQueue();
    if (!q.length) {
      drainingRef.current = false;
      setDraining(false);
      return { drained: 0, failed: 0 };
    }
    let drained = 0, failed = 0;
    const remaining = [];
    for (const item of q) {
      try {
        const r = await fetch(item.url, {
          method: item.method || "POST",
          headers: item.headers || {},
          body: item.body,
          credentials: item.credentials || "include",
        });
        if (r.ok || r.status < 500) drained += 1;
        else { failed += 1; remaining.push(item); }
      } catch {
        failed += 1;
        remaining.push(item);
      }
    }
    await writeQueue(remaining);
    const result = { drained, failed, at: new Date().toISOString(), via: "in-tab" };
    setLastDrainResult(result);
    drainingRef.current = false;
    setDraining(false);
    return result;
  }, [readQueue, writeQueue]);

  // Auto-drain when we come back online
  useEffect(() => {
    if (isOnline && queueSize > 0 && !drainingRef.current) {
      const t = setTimeout(() => { drain(); }, 800);
      return () => clearTimeout(t);
    }
  }, [isOnline, queueSize, drain]);

  return { queuedFetch, cacheGet, cacheSet, queueSize, isOnline, draining, drain, lastDrainResult };
}

function safeStringify(v) {
  if (v === undefined || v === null) return "";
  try { return JSON.stringify(v); } catch { return ""; }
}

function synthQueuedResponse() {
  return {
    ok: true,
    status: 202,
    _queued: true,
    json: async () => ({ ok: true, queued: true, note: "Offline — queued locally, will sync when back online." }),
    text: async () => "queued",
  };
}
